import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../src/config/api';
import { contactEmail } from './constants';

interface ContactFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    highlightColor: {
        primary: string;
        hover: string;
        gradient: {
            from: string;
            to: string;
            fromLight: string;
            toGray: string;
        };
    };
}

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

/**
 * Splits a single name string into first/last for the Zoho Contacts API.
 * If only one word is given, it becomes the last name (Zoho requires Last_Name).
 */
function splitName(fullName: string): { firstName: string; lastName: string } {
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 1) return { firstName: '', lastName: parts[0] };
    return { firstName: parts.slice(0, -1).join(' '), lastName: parts[parts.length - 1] };
}

const ContactFormModal: React.FC<ContactFormModalProps> = ({ isOpen, onClose, highlightColor }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        projectType: '',
        message: ''
    });
    const [status, setStatus] = useState<SubmitStatus>('idle');
    const [errorMsg, setErrorMsg] = useState('');

    // Lock body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    // Reset form when modal opens
    useEffect(() => {
        if (isOpen) {
            setFormData({ name: '', email: '', projectType: '', message: '' });
            setStatus('idle');
            setErrorMsg('');
        }
    }, [isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus('submitting');
        setErrorMsg('');

        const { firstName, lastName } = splitName(formData.name);
        if (!lastName) {
            setStatus('error');
            setErrorMsg('Please enter your name.');
            return;
        }

        try {
            const res = await fetch(getApiUrl('/api/contact-form/'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    firstName,
                    lastName,
                    email: formData.email,
                    projectType: formData.projectType,
                    message: formData.message,
                }),
            });

            const data = await res.json();

            if (res.ok && data.success) {
                setStatus('success');
                setFormData({ name: '', email: '', projectType: '', message: '' });
            } else {
                setStatus('error');
                setErrorMsg(data.error || 'Something went wrong. Please try again.');
            }
        } catch {
            setStatus('error');
            setErrorMsg('Network error. Please try again.');
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 animate-in fade-in duration-300"
            onClick={onClose}
        >
            <style>{`
                .modal-contact-field::placeholder {
                    color: #6b7280;
                }
                .modal-contact-field option {
                    background-color: #0d0d0d;
                    color: white;
                }
                .modal-contact-field:-webkit-autofill,
                .modal-contact-field:-webkit-autofill:hover,
                .modal-contact-field:-webkit-autofill:focus,
                .modal-contact-field:-webkit-autofill:active {
                    -webkit-box-shadow: 0 0 0 30px #0d0d0d inset !important;
                    -webkit-text-fill-color: white !important;
                    transition: background-color 5000s ease-out 0s;
                    border-color: #2a2a2a !important;
                }
            `}</style>
            <div
                className="relative w-full max-w-2xl bg-neutral-900 overflow-hidden border border-neutral-800 animate-in zoom-in-95 duration-300 max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="sticky top-0 bg-neutral-900 border-b border-neutral-800 p-6 z-10">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 p-2 rounded-full bg-black/50 hover:bg-black text-white transition-all duration-300 hover:scale-110 cursor-pointer"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                    <h2 className="text-3xl font-bold text-white pr-12">Book a Demo</h2>
                    <p className="text-gray-400 text-sm mt-2">
                        Fill out the form below and we'll get back to you shortly. Or email us at {contactEmail}
                    </p>
                </div>

                {/* Content */}
                <div className="overflow-y-auto p-6">
                    {status === 'success' ? (
                        <div className="text-center py-12">
                            <svg className="w-16 h-16 mx-auto mb-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <h3 className="text-2xl font-bold text-white mb-2">Thank you!</h3>
                            <p className="text-gray-300">We'll be in touch soon to schedule your demo.</p>
                            <button
                                onClick={onClose}
                                className="mt-6 px-6 py-3 text-sm font-semibold transition-all duration-200 cursor-pointer"
                                style={{
                                    backgroundColor: 'transparent',
                                    color: 'white',
                                    border: '1px solid white',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'white';
                                    e.currentTarget.style.color = 'black';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = 'white';
                                }}
                            >
                                Close
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label htmlFor="modal-name" className="block text-gray-400 text-sm font-medium mb-2">Name</label>
                                <input
                                    type="text"
                                    id="modal-name"
                                    required
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="modal-contact-field w-full px-4 py-3 text-white focus:outline-none transition-all duration-200"
                                    style={{ backgroundColor: '#0d0d0d', border: '1px solid #2a2a2a' }}
                                    placeholder="Your name"
                                    onFocus={(e) => {
                                        e.currentTarget.style.backgroundColor = '#1a1a1a';
                                        e.currentTarget.style.borderColor = '#e0e0e0';
                                    }}
                                    onBlur={(e) => {
                                        e.currentTarget.style.backgroundColor = '#0d0d0d';
                                        e.currentTarget.style.borderColor = '#2a2a2a';
                                    }}
                                />
                            </div>

                            <div>
                                <label htmlFor="modal-email" className="block text-gray-400 text-sm font-medium mb-2">Email</label>
                                <input
                                    type="email"
                                    id="modal-email"
                                    required
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    className="modal-contact-field w-full px-4 py-3 text-white focus:outline-none transition-all duration-200"
                                    style={{ backgroundColor: '#0d0d0d', border: '1px solid #2a2a2a' }}
                                    placeholder="you@example.com"
                                    onFocus={(e) => {
                                        e.currentTarget.style.backgroundColor = '#1a1a1a';
                                        e.currentTarget.style.borderColor = '#e0e0e0';
                                    }}
                                    onBlur={(e) => {
                                        e.currentTarget.style.backgroundColor = '#0d0d0d';
                                        e.currentTarget.style.borderColor = '#2a2a2a';
                                    }}
                                />
                            </div>

                            <div>
                                <label htmlFor="modal-projectType" className="block text-gray-400 text-sm font-medium mb-2">Your Team</label>
                                <select
                                    id="modal-projectType"
                                    required
                                    value={formData.projectType}
                                    onChange={(e) => setFormData({ ...formData, projectType: e.target.value })}
                                    className="modal-contact-field w-full px-4 py-3 focus:outline-none transition-all duration-200"
                                    style={{ backgroundColor: '#0d0d0d', border: '1px solid #2a2a2a', color: formData.projectType ? 'white' : '#6b7280', appearance: 'none', backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e\")", backgroundPosition: 'right 0.75rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em', paddingRight: '3rem' }}
                                    onFocus={(e) => {
                                        e.currentTarget.style.backgroundColor = '#1a1a1a';
                                        e.currentTarget.style.borderColor = '#e0e0e0';
                                    }}
                                    onBlur={(e) => {
                                        e.currentTarget.style.backgroundColor = '#0d0d0d';
                                        e.currentTarget.style.borderColor = '#2a2a2a';
                                    }}
                                >
                                    <option value="">Select a Team</option>
                                    <option value="Ncaa">NCAA</option>
                                    <option value="Gym">Gym</option>
                                    <option value="Clinic">Clinic</option>
                                    <option value="Personal">Personal</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>

                            <div>
                                <label htmlFor="modal-message" className="block text-gray-400 text-sm font-medium mb-2">Your message (optional)</label>
                                <textarea
                                    id="modal-message"
                                    rows={4}
                                    value={formData.message}
                                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                    className="modal-contact-field w-full px-4 py-3 text-white focus:outline-none transition-all duration-200 resize-none"
                                    style={{ backgroundColor: '#0d0d0d', border: '1px solid #2a2a2a' }}
                                    placeholder="Tell us about your team or organization..."
                                    onFocus={(e) => {
                                        e.currentTarget.style.backgroundColor = '#1a1a1a';
                                        e.currentTarget.style.borderColor = '#e0e0e0';
                                    }}
                                    onBlur={(e) => {
                                        e.currentTarget.style.backgroundColor = '#0d0d0d';
                                        e.currentTarget.style.borderColor = '#2a2a2a';
                                    }}
                                />
                            </div>

                            {status === 'error' && (
                                <p className="text-red-400 text-sm">{errorMsg}</p>
                            )}

                            <button
                                type="submit"
                                disabled={status === 'submitting'}
                                className="w-full px-8 py-4 mt-12 text-sm font-semibold transition-all duration-200 ease-out cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{
                                    backgroundColor: 'transparent',
                                    color: 'white',
                                    border: '1px solid white',
                                    borderRadius: '0',
                                    boxShadow: 'none'
                                }}
                                onMouseEnter={(e) => {
                                    if (status !== 'submitting') {
                                        e.currentTarget.style.backgroundColor = 'white';
                                        e.currentTarget.style.color = 'black';
                                        e.currentTarget.style.boxShadow = '0 2px 10px -1px rgba(0, 0, 0, 0.1), 0 2px 8px -1px rgba(0, 0, 0, 0.06)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = 'white';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                            >
                                {status === 'submitting' ? 'SENDING...' : 'BOOK A DEMO'}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ContactFormModal;
